import { PROPERTY_GRID_SUBJECT_PROPERTY, Site, getSiteDisplay } from '@aloft/toolkit-shared';
import { Flex, Typography } from '@aloft/toolkit-sui';

import { useWatch } from 'react-hook-form';

import { AdjustmentField } from '../../../components/PropertyGrid/fields/AdjustmentField.tsx';
import { EditableUnitRateField } from '../../../components/PropertyGrid/fields/EditableUnitRateField.tsx';
import { LabelField } from '../../../components/PropertyGrid/fields/LabelField.tsx';
import { ReadOnlyField } from '../../../components/PropertyGrid/fields/ReadOnlyField.tsx';
import { ReadOnlyUnitRateField } from '../../../components/PropertyGrid/fields/ReadOnlyUnitRateField.tsx';
import { readOnlyFieldTypographyProps } from '../../../components/PropertyGrid/fields/styles.ts';
import { getAloftAreaJSONPropertyGridField } from '../../../components/PropertyGrid/fields/utils.tsx';
import { TitleRow } from '../../../components/PropertyGrid/rows/TitleRow.tsx';
import {
  PropertyGridField,
  PropertyGridSectionProps,
} from '../../../components/PropertyGrid/types.ts';
import { selectSalesComparisonApproach } from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';

export const AdjustmentsGridSiteExtractionSection = ({
  columnIndex,
  fieldTypes,
  control,
  getValues,
  setValue,
}: PropertyGridSectionProps) => {
  const isUnitRate = useAppSelector(selectSalesComparisonApproach) === 'unit_rate';

  const propertyId = useWatch({
    name: `columns.${columnIndex}.propertyId`,
    control,
  });

  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;
  const showLabels = fieldTypes.includes('label');
  const showValues =
    fieldTypes.includes('readOnlyValue') || fieldTypes.includes('readOnlyArvValue');
  const showAdjustments = fieldTypes.includes('adjustment');
  const showUnitRate = fieldTypes.includes('editableUnitRate') && isSubject;
  const showReadOnlyUnitRate = fieldTypes.includes('readOnlyUnitRate');

  let colSpan = 1;
  if (isSubject) {
    const hasArvValue = fieldTypes.includes('readOnlyArvValue');
    const hasAivValue = fieldTypes.includes('readOnlyValue');
    colSpan = hasArvValue && hasAivValue ? 2 : 1;
  }

  const influenceField: PropertyGridField<Site> = {
    fieldName: 'editableData.site',
    label: 'Site Influence',
    toDisplayValue: (site) => getSiteDisplay(site).influence,
  };

  const viewField: PropertyGridField<Site> = {
    fieldName: 'editableData.site',
    label: 'View',
    toDisplayValue: (site) => getSiteDisplay(site).view,
  };

  return (
    <>
      <TitleRow
        columnIndex={columnIndex}
        fieldTypes={fieldTypes}
        title="Site Extraction Adjustments"
        getValues={getValues}
        control={control}
      />
      {showLabels && (
        <LabelField
          columnIndex={columnIndex}
          field={{
            fieldName: 'editableData.site.area',
            label: 'Site Size',
            manualAdjustmentKey: 'site',
          }}
          control={control}
          getValues={getValues}
          setValue={setValue}
        />
      )}
      {showValues && (
        <ReadOnlyField
          columnIndex={columnIndex}
          field={getAloftAreaJSONPropertyGridField({
            fieldName: 'editableData.site.area',
            label: 'Site Size',
          })}
          colSpan={colSpan}
          control={control}
          getValues={getValues}
          setValue={setValue}
        />
      )}
      {showUnitRate && (
        <EditableUnitRateField
          columnIndex={columnIndex}
          field={{
            fieldName: 'editableData.site.area',
            label: 'Site Size',
            manualAdjustmentKey: 'site',
            unitRateKey: 'siteSize',
            unit: 'sf',
          }}
          control={control}
          getValues={getValues}
          setValue={setValue}
        />
      )}
      {showReadOnlyUnitRate && (
        <ReadOnlyUnitRateField
          columnIndex={columnIndex}
          field={{
            fieldName: 'editableData.site.area',
            label: 'Site Size',
            unitRateKey: 'siteSize',
            unit: 'sf',
          }}
          control={control}
          getValues={getValues}
          setValue={setValue}
        />
      )}
      {showAdjustments && (
        <Flex width="100%" gridColumn={isSubject ? undefined : '2'}>
          <AdjustmentField
            columnIndex={columnIndex}
            field={{
              fieldName: 'editableData.site',
              label: 'Site Size',
              manualAdjustmentKey: 'site',
            }}
            control={control}
            getValues={getValues}
            setValue={setValue}
          />
        </Flex>
      )}
      {showLabels && (
        <LabelField
          columnIndex={columnIndex}
          field={{
            fieldName: 'editableData.site.influence',
            label: 'Site Influence',
            manualAdjustmentKey: isUnitRate ? 'siteInfluence' : 'site',
          }}
          control={control}
          getValues={getValues}
          setValue={setValue}
        />
      )}
      {showValues && (
        <ReadOnlyField
          columnIndex={columnIndex}
          field={influenceField}
          // eslint-disable-next-line no-nested-ternary
          colSpan={isSubject ? colSpan : isUnitRate ? 1 : 2}
          control={control}
          getValues={getValues}
          setValue={setValue}
        />
      )}
      {showAdjustments && isUnitRate && (
        <Flex width="100%" gridColumn={isSubject ? undefined : '2'}>
          <AdjustmentField
            columnIndex={columnIndex}
            field={{
              fieldName: 'editableData.site.influence',
              label: 'Site Influence',
              manualAdjustmentKey: 'siteInfluence',
            }}
            control={control}
            getValues={getValues}
            setValue={setValue}
          />
        </Flex>
      )}
      {(showReadOnlyUnitRate || (!showLabels && isUnitRate && isSubject)) && (
        <Typography {...readOnlyFieldTypographyProps}>-</Typography>
      )}
      {showLabels && (
        <LabelField
          columnIndex={columnIndex}
          field={{
            fieldName: 'editableData.site.view',
            label: 'View',
            manualAdjustmentKey: isUnitRate ? 'siteView' : 'site',
          }}
          control={control}
          getValues={getValues}
          setValue={setValue}
        />
      )}
      {showValues && (
        <ReadOnlyField
          columnIndex={columnIndex}
          field={viewField}
          // eslint-disable-next-line no-nested-ternary
          colSpan={isSubject ? colSpan : isUnitRate ? 1 : 2}
          control={control}
          getValues={getValues}
          setValue={setValue}
        />
      )}
      {showAdjustments && isUnitRate && (
        <Flex width="100%" gridColumn={isSubject ? undefined : '2'}>
          <AdjustmentField
            columnIndex={columnIndex}
            field={{
              fieldName: 'editableData.site.view',
              label: 'View',
              manualAdjustmentKey: 'siteView',
            }}
            control={control}
            getValues={getValues}
            setValue={setValue}
          />
        </Flex>
      )}
      {(showReadOnlyUnitRate || (!showLabels && isUnitRate && isSubject)) && (
        <Typography {...readOnlyFieldTypographyProps}>-</Typography>
      )}
    </>
  );
};
